/**
 * useUnlockLink — the unauthenticated unlock page's data layer.
 *
 * Owns the one piece of state that is easy to get wrong here: **where the token lives after
 * the page loads.**
 *
 * The token arrives in the URL. Leaving it there puts a live door credential in the address
 * bar, in browser history, and in any `Referer` the page might emit — so it is moved out with
 * `history.replaceState` immediately. But then a refresh has nothing to exchange, and the
 * usage pattern makes a refresh likely: tap the link, switch back to Messages to re-read the
 * text, return to a tab the OS has since evicted and reloaded.
 *
 * So it is stashed in `sessionStorage` first. That is per-tab, cleared when the tab closes,
 * never in history, and survives both a reload and a tab restore.
 *
 * @module services/internalApi/access/hooks/useUnlockLink
 */

import { useCallback, useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { queryKeys } from '~/lib/query/keys';
import { cachePresets } from '~/lib/query/cachePresets';
import { useClubId, useTenantHandle } from '~/services/appRuntime';
import { resolveUnlockLink, unlockWithLink } from '../api/accessLinkService';
import type { UnlockDoorResponse } from '../types';

const TOKEN_STORAGE_KEY = 'butter.unlockToken';

/**
 * Take the token from the URL, stash it, and scrub the address bar.
 *
 * Runs before the first render rather than in an effect: an effect would leave the token
 * visible for a frame, and anything that reads `location.href` in between — an analytics
 * snippet, an error report — would capture it.
 */
function claimToken(urlToken: string | undefined): string | null {
  if (urlToken) {
    try {
      sessionStorage.setItem(TOKEN_STORAGE_KEY, urlToken);
    } catch {
      // Private browsing, or storage disabled. The exchange below still works for this
      // page view; only surviving a refresh is lost.
    }
    return urlToken;
  }

  try {
    return sessionStorage.getItem(TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * A short, stable, non-reversible stand-in for the token, used only as a cache key.
 *
 * djb2 — not a security primitive and not pretending to be one. The token is already the
 * credential and is already in memory; this exists so the KEY is not, because query keys are
 * what a persister would write to disk.
 */
function tokenFingerprint(token: string): string {
  let hash = 5381;
  for (let i = 0; i < token.length; i++) {
    hash = ((hash << 5) + hash + token.charCodeAt(i)) | 0;
  }
  return (hash >>> 0).toString(36);
}

export function useUnlockLink(urlToken: string | undefined) {
  const [token] = useState(() => claimToken(urlToken));
  const [, setClubId] = useClubId();
  const [, setTenantHandle] = useTenantHandle();
  const [unlockResult, setUnlockResult] = useState<UnlockDoorResponse | null>(null);

  // Scrub the URL once the token is safely in sessionStorage. `replaceState`, not
  // `pushState` — a history entry per unlock is a back-button trap. Raw, outside
  // `~/lib/navigation` by design: this rewrites the PATH (`/unlock/:token` →
  // `/unlock`), and a router navigation between the two routes would remount
  // UnlockPage mid-exchange.
  useEffect(() => {
    if (!urlToken) return;
    window.history.replaceState(null, '', '/unlock');
  }, [urlToken]);

  const exchange = useQuery({
    queryKey: token
      ? queryKeys.unlockLink.exchange(tokenFingerprint(token))
      : queryKeys.disabled.unlockLink(),
    queryFn: () => resolveUnlockLink(token!),
    enabled: !!token,
    // A credential exchange, not reference data — it must not inherit the 5-minute `stable`
    // default. The result is only meaningful for this page view.
    ...cachePresets.realTime,
    // The window is minutes long and the page is open for seconds. Refetching on focus would
    // re-run the exchange every time the member switches back from Messages.
    refetchOnWindowFocus: false,
    retry: false,
  });

  // The exchange is what tells this page which tenant and club it belongs to — there is
  // nothing in the URL to read it from. Written into appRuntime so branding resolves and
  // apiClient's tenant/club headers work from here on.
  useEffect(() => {
    if (!exchange.data) return;
    setClubId(String(exchange.data.clubId));
    if (exchange.data.tenantHandle) setTenantHandle(exchange.data.tenantHandle);
  }, [exchange.data, setClubId, setTenantHandle]);

  const unlock = useMutation({
    mutationFn: () => unlockWithLink(token!),
    // No retry. From the admin per-door work: "a single click could become four actuations —
    // each physically reopening a door that already opened, and each writing another audit
    // row". The member is told to try again; that is their decision to make, not ours.
    retry: false,
    // A new attempt starts clean: a partial result from the last one must not stay on screen
    // under this one's error line.
    onMutate: () => setUnlockResult(null),
    onSuccess: setUnlockResult,
  });

  const dismissResult = useCallback(() => setUnlockResult(null), []);

  return {
    /** No token in the URL and none stashed — the page was opened directly. */
    missingToken: !token,
    access: exchange.data ?? null,
    isLoading: exchange.isLoading,
    error: exchange.error,
    /**
     * Re-run the exchange. Offered ONLY for a server fault — a refusal will refuse again, and
     * a retry button under "this link isn't active" would just be a lie with a shorter delay.
     */
    retry: exchange.refetch,
    unlock,
    unlockResult,
    dismissResult,
  };
}
